import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ClipboardDocumentCheckIcon,
  PlayIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ArrowDownTrayIcon,
  PlusIcon,
  XMarkIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { craApi, hmdaApi, teamApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';
import { ModuleIntro } from '../components/ui';

/** True when the HMDA validation's transmittal sheet didn't carry a parseable calendar year. */
function isYearMissing(calendarYear: unknown): boolean {
  if (!calendarYear) return true;
  const n = Number(calendarYear);
  return !Number.isFinite(n) || n < 2018 || n > new Date().getFullYear();
}

interface TierDistribution {
  low: number;
  moderate: number;
  middle: number;
  upper: number;
  unknown: number;
}

interface HmdaLendingTestRow {
  assessmentAreaId: string | null;
  assessmentAreaName: string;
  bank: {
    productLabel: string;
    counts: TierDistribution;
    pcts: TierDistribution;
    total: number;
    amounts: TierDistribution;
    amountPcts: TierDistribution;
    totalAmount: number;
  };
  demographic: {
    label: string;
    pcts: TierDistribution | null;
    methodology: string;
    sourceNote?: string;
  };
  marketBenchmark: {
    label: string;
    pcts: TierDistribution | null;
    methodology: string;
    pending?: boolean;
  };
  conclusion: string | null;
}

// CRA convention (Wallace 2026-04-28): percentages to 1 decimal place,
// total cells display the literal "100%" rather than "100.0%". Fair Lending
// uses 2 decimals (handled in FLD components, not here).
const fmtPct = (v: number | null | undefined): string =>
  v == null ? '—' : `${(v * 100).toFixed(1)}%`;

const fmtCurrency = (v: number): string =>
  `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

/**
 * Color-code a comparator (demographic / market benchmark) cell against the
 * bank's value in the same LMI tier (Wallace 2026-04-28 spec):
 *   red    when benchmark > bank (bank is underperforming the benchmark)
 *   green  when benchmark < bank (bank is outperforming the benchmark)
 *   black  when equal (or either side missing)
 * Only applied to LOW and MODERATE tiers — Middle/Upper/Unknown stay neutral.
 */
const lmiCompareClass = (
  benchmarkPct: number | null | undefined,
  bankPct: number | null | undefined,
): string => {
  if (benchmarkPct == null || bankPct == null) return 'text-gray-700';
  if (benchmarkPct > bankPct) return 'text-red-600 font-medium';
  if (benchmarkPct < bankPct) return 'text-green-600 font-medium';
  return 'text-gray-700';
};

const conclusionColor = (c: string | null): string => {
  if (!c) return 'bg-gray-100 text-gray-700';
  switch (c) {
    case 'Excellent':
    case 'A Substantial Majority':
      return 'bg-green-100 text-green-700';
    case 'Reasonable':
    case 'Majority Inside':
      return 'bg-blue-100 text-blue-700';
    case 'Poor':
    case 'Majority Outside':
      return 'bg-amber-100 text-amber-700';
    case 'Very Poor':
    case 'Substantial Majority Outside':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

/**
 * CRA Micro Data products beyond SB/SF (Michael Wallace, 2026-10-07): Consumer is
 * Types of Loan 5-8 COMBINED into one set of tables; 03 and 09 each get their own,
 * bank distribution only.
 */
type ConsumerProduct = 'CONSUMER' | 'OTHER_SB' | 'OTHER_LOAN';
const CONSUMER_PRODUCTS: ConsumerProduct[] = ['CONSUMER', 'OTHER_SB', 'OTHER_LOAN'];
const CONSUMER_LABEL: Record<ConsumerProduct, string> = {
  CONSUMER: 'Consumer',
  OTHER_SB: 'Other Loans for Small Business Purposes',
  OTHER_LOAN: 'Other Loan Data',
};
const CONSUMER_SCOPE: Record<ConsumerProduct, string> = {
  CONSUMER: 'Types of Loan 05–08 combined: Motor Vehicle, Credit Card, Other Secured, Other Unsecured.',
  OTHER_SB: 'Type of Loan 03. Bank distribution only — no comparator.',
  OTHER_LOAN: 'Type of Loan 09. Bank distribution only — no comparator.',
};
type ItemProduct = 'HMDA' | 'SB' | 'SF' | ConsumerProduct;
const itemLabel = (p: ItemProduct) => ((CONSUMER_PRODUCTS as string[]).includes(p) ? CONSUMER_LABEL[p as ConsumerProduct] : p);
interface LendingItem { product: ItemProduct; year: number; validationId?: string }

/* ── Consumer / 03 / 09 GD & BPD ─────────────────────────────────────────── */
type BandKey = 'Low' | 'Moderate' | 'Middle' | 'Upper' | 'Unknown';
const BAND_KEYS: BandKey[] = ['Low', 'Moderate', 'Middle', 'Upper', 'Unknown'];
interface BandCounts { count: Record<BandKey, number>; amount: Record<BandKey, number>; totalCount: number; totalAmount: number }
/** Consumer comparators — households (Michael Wallace, 2026-10-07). */
interface HouseholdsByTract { pct: Record<BandKey, number>; households: number; sourceYear: number }
interface HouseholdsByIncome {
  pct: Record<Exclude<BandKey, 'Unknown'>, number>;
  households: number;
  medianHouseholdIncome: number | null;
  sourceYear: number;
}
interface ConsumerYearRow {
  year: number;
  geographic: BandCounts;
  borrower: BandCounts | null;
  householdsByTract: HouseholdsByTract | null;
  householdsByIncome: HouseholdsByIncome | null;
}
/** A comparator row under a band table: a label, a detail, and a share per band. */
interface ComparatorRow { label: string; detail: string; pct: Partial<Record<BandKey, number>> }
interface ConsumerProductResult {
  product: ConsumerProduct;
  label: string;
  hasBorrowerProfile: boolean;
  assessmentAreas: Array<{ assessmentAreaName: string; years: Array<{ year: number; row: ConsumerYearRow | null }> }>;
}
interface ConsumerLendingResult { years: number[]; products: ConsumerProductResult[] }
interface LtdSummary {
  bankAvgPct: number | null;
  peerComparisonAvgPct: number | null;
  peerAvgPct: number | null;
  peers: Array<{ cert: number; name: string | null; avgPct: number | null }>;
  windowStart: string | null;
  windowEnd: string | null;
  conclusion: string | null;
}
interface FullLendingResult {
  concentration: { rows: MatrixRow[]; combined: MatrixRow };
  multiYear: { years: number[]; geographicDistribution: MultiYearAaBlock[]; borrowerProfileDistribution: MultiYearAaBlock[] };
  sbSf?: SbSfLendingResult;
  consumer?: ConsumerLendingResult;
  ltdSummary?: LtdSummary | null;
  /** CRA bank category; a Large Bank has no LTD criterion. */
  bankCategory?: 'SMALL' | 'INTERMEDIATE' | 'LARGE' | null;
}

/* ── SB/SF GD & BPD (bank data) — Michael Wallace 2026-07-26 ──────────────── */
type IncomeLevel = 'Low' | 'Moderate' | 'Middle' | 'Upper' | 'Unknown';
type GarCode = '1' | '2' | '3';
interface SbSfGdAggregate { lowPct: number | null; moderatePct: number | null; middlePct: number | null; upperPct: number | null; unknownPct: number | null; sourceYear: number; source: 'ffiec' | 'entered' }
interface SbSfBpdAggregate { garLessThan1MPct: number | null; sourceYear: number; source: 'ffiec' | 'entered' }
const aggSourceTag = (s: 'ffiec' | 'entered') => (s === 'ffiec' ? 'FFIEC CRA Aggregate' : 'entered');
interface SbSfGdYear {
  year: number;
  distribution: Record<IncomeLevel, number>;
  total: number;
  aggregate: SbSfGdAggregate | null;
}
interface SbSfBpdYear {
  year: number;
  topTierLabel: string;
  tierKeys: string[];
  byGarAndAmount: Record<GarCode, Record<string, number>>;
  tierTotals: Record<string, number>;
  garTotals: Record<GarCode, number>;
  total: number;
  aggregate: SbSfBpdAggregate | null;
}
interface SbSfAaBlock<T> { assessmentAreaName: string; years: Array<{ year: number; row: T | null }> }
interface SbSfProductResult {
  product: 'SB' | 'SF';
  geographicDistribution: Array<SbSfAaBlock<SbSfGdYear>>;
  borrowerProfileDistribution: Array<SbSfAaBlock<SbSfBpdYear>>;
}
interface SbSfLendingResult { years: number[]; products: SbSfProductResult[]; generatedAt: string }

const PRODUCT_LABEL: Record<'SB' | 'SF', string> = { SB: 'Small Business', SF: 'Small Farm' };
const GAR_ROWS: Array<{ code: GarCode; label: string }> = [
  { code: '1', label: '$1 Million or Less' },
  { code: '2', label: 'Greater than $1 Million/Unknown' },
  { code: '3', label: 'Unknown' },
];
const GD_INCOME_LEVELS: IncomeLevel[] = ['Low', 'Moderate', 'Middle', 'Upper', 'Unknown'];
const GD_AGG_KEY: Record<IncomeLevel, 'lowPct' | 'moderatePct' | 'middlePct' | 'upperPct' | 'unknownPct'> = {
  Low: 'lowPct', Moderate: 'moderatePct', Middle: 'middlePct', Upper: 'upperPct', Unknown: 'unknownPct',
};

export default function CradHmdaLendingTestPage() {
  const { teamId, isLoading: teamLoading } = useTeamId();
  // The bank's CRA category (Large Bank: no LTD criterion). Same query as the peer editor: the raw config row.
  const { data: craConfig } = useQuery({
    queryKey: ['cra-config', teamId],
    queryFn: async () => (await craApi.getConfig(teamId!)).data,
    enabled: !!teamId,
  });
  const isLargeBank = craConfig?.bankCategory === 'LARGE';
  // Unified multi-year / multi-product selection driving the whole report (Michael Wallace
  // 2026-06-07 — one entry point; CRA evaluations typically span multiple years/products).
  const [items, setItems] = useState<LendingItem[]>([]);
  const [result, setResult] = useState<FullLendingResult | null>(null);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingWord, setExportingWord] = useState(false);
  const [draftProduct, setDraftProduct] = useState<ItemProduct | 'SBSF' | 'ALL_CRA'>('HMDA');
  const [draftValidation, setDraftValidation] = useState('');
  const [draftYear, setDraftYear] = useState('');
  const [showDollar, setShowDollar] = useState(false); // toggle $ volume rows in GD/BPD (Wallace)
  // Source / Note column on the HMDA GD/BPD tables. Off by default, and the Word
  // export follows it — Michael Wallace (2026-10-08): toggle it online, suppress it
  // in Word unless the advisor chooses to include it.
  const [showSourceNote, setShowSourceNote] = useState(false);

  const { data: validationsData } = useQuery({
    queryKey: ['hmda-validations'],
    queryFn: async () => {
      const res = await hmdaApi.getValidations({ limit: 20 });
      return res.data;
    },
  });
  const validations = useMemo(() => validationsData?.validations || [], [validationsData?.validations]);

  // Which HMDA years currently have a SAVED market aggregate (so "is 2025 pulled?" is answerable
  // at a glance, not just via the transient Apply toast).
  const { data: aggregateStatusData, refetch: refetchAggregateStatus } = useQuery({
    queryKey: ['hmda-aggregate-status', teamId],
    queryFn: async () => (await craApi.getHmdaAggregateStatus(teamId!)).data,
    enabled: !!teamId,
  });
  const savedAggregateYears = aggregateStatusData?.years ?? [];

  const runMutation = useMutation({
    mutationFn: () => craApi.getFullLendingTest(teamId!, items),
    onSuccess: (response) => {
      setResult(response.data as FullLendingResult);
      toast.success('Lending Test computed');
    },
    onError: (err: any) => {
      const detail = err?.response?.data?.error || err?.message;
      toast.error(detail ? `Failed: ${detail}` : 'Failed to compute Lending Test', { duration: 8000 });
    },
  });

  // Selected HMDA years (for the market-aggregate pull, which is HMDA + per-year).
  const hmdaYears = useMemo(
    () => [...new Set(items.filter(i => i.product === 'HMDA').map(i => i.year))].sort((a, b) => b - a),
    [items],
  );

  // Market-aggregate pull from the public CFPB Data Browser, for every selected HMDA year.
  // Preview first (reconcile), then Apply to populate the Geographic / Borrower's Profile market rows.
  const pullAggregateMutation = useMutation({
    mutationFn: async ({ persist }: { persist: boolean }) => {
      const results: any[] = [];
      for (const year of hmdaYears) results.push((await craApi.pullHmdaAggregate(teamId!, year, persist)).data);
      return { results, persist };
    },
    onSuccess: ({ results, persist }) => {
      const allAreas = results.flatMap((r: any) => r.areas || []);
      const failed = allAreas.filter((a: any) => a.note && (a.total || 0) === 0);
      const totalLoans = allAreas.reduce((s: number, x: any) => s + (x.total || 0), 0);

      // Every area failed → this is NOT a "0 loans applied" success. Surface it as an error and point
      // to the per-area notes, which now carry the real reason — a large-county timeout vs. a year not
      // yet published (Michael Wallace, #443).
      if (allAreas.length > 0 && failed.length === allAreas.length) {
        toast.error(
          `CFPB aggregate couldn't be loaded for any assessment area — see the note on each table for the reason (e.g. a large-county timeout vs. a year not yet published).`,
          { duration: 10000 },
        );
        return;
      }
      if (failed.length > 0) {
        toast(`Loaded ${totalLoans.toLocaleString()} CFPB loans, but ${failed.length} of ${allAreas.length} area(s) failed — see their table notes.`, { icon: '⚠️', duration: 9000 });
      }
      if (persist) {
        toast.success(`Applied ${totalLoans.toLocaleString()} CFPB market loans across ${hmdaYears.length} year(s).`);
        refetchAggregateStatus();
        if (items.length > 0) runMutation.mutate();
      } else {
        toast.success(`Preview: ${totalLoans.toLocaleString()} CFPB market loans across ${hmdaYears.length} year(s). Reconcile, then Apply.`, { duration: 7000 });
      }
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error || 'CFPB pull failed. See the note on each table for the reason.', { duration: 8000 }),
  });

  const triggerDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportExcel = async () => {
    if (!teamId || items.length === 0) return;
    setExportingExcel(true);
    try {
      const res = await craApi.exportLendingTestExcel(teamId, items);
      triggerDownload(res.data as Blob, `lending-test-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Excel export failed');
    } finally {
      setExportingExcel(false);
    }
  };

  const handleExportWord = async () => {
    if (!teamId || items.length === 0) return;
    setExportingWord(true);
    try {
      const res = await craApi.exportLendingTestWord(teamId, items, showSourceNote);
      triggerDownload(res.data as Blob, `lending-test-${new Date().toISOString().slice(0, 10)}.docx`);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Word export failed');
    } finally {
      setExportingWord(false);
    }
  };

  const addItem = () => {
    const year = Number(draftYear);
    if (!Number.isFinite(year) || year < 2018) { toast.error('Enter a valid year (>= 2018)'); return; }
    if (draftProduct === 'HMDA' && !draftValidation) { toast.error('Select an HMDA validation'); return; }
    // "All CRA" adds every major product for the year in one click: HMDA (when a
    // validation is picked), small business, small farm, and consumer. Types 03 and 09
    // stay individual picks — rare, and a zero row for each would clutter AA Concentration.
    if (draftProduct === 'ALL_CRA') {
      const wanted: LendingItem[] = [
        ...(draftValidation ? [{ product: 'HMDA' as const, year, validationId: draftValidation }] : []),
        { product: 'SB', year },
        { product: 'SF', year },
        { product: 'CONSUMER', year },
      ];
      const toAdd = wanted.filter(w => !items.some(i => i.product === w.product && i.year === year));
      if (toAdd.length === 0) { toast.error(`${year} All CRA products are already added`); return; }
      setItems([...items, ...toAdd]);
      setDraftValidation('');
      if (!draftValidation) toast(`HMDA not added for ${year} — select an HMDA validation to include it.`);
      return;
    }
    // "Small Business & Small Farm" adds BOTH products for the year — banks often keep SB + SF in one
    // register, so this is one click instead of two (Michael Wallace, 2026-07-27).
    if (draftProduct === 'SBSF') {
      const toAdd = (['SB', 'SF'] as const).filter(p => !items.some(i => i.product === p && i.year === year));
      if (toAdd.length === 0) { toast.error(`${year} Small Business & Small Farm already added`); return; }
      setItems([...items, ...toAdd.map(p => ({ product: p, year }))]);
      return;
    }
    if (items.some(i => i.product === draftProduct && i.year === year)) { toast.error(`${year} ${itemLabel(draftProduct)} is already added`); return; }
    setItems([...items, { product: draftProduct, year, validationId: draftProduct === 'HMDA' ? draftValidation : undefined }]);
    setDraftValidation('');
  };
  const removeItem = (idx: number) => setItems(items.filter((_, i) => i !== idx));
  // When an HMDA validation is picked, default the year to its detected calendar year.
  const onPickValidation = (id: string) => {
    setDraftValidation(id);
    const v = validations.find((x: any) => x.id === id);
    if (v && !isYearMissing(v.calendarYear)) setDraftYear(String(v.calendarYear));
  };

  if (teamLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-64 bg-gray-200 rounded" />
        <div className="h-96 bg-gray-200 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Link to="/cra" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-3">
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to CRA Dashboard
        </Link>
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-ocean-50 p-2 text-ocean-600">
            <ClipboardDocumentCheckIcon className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-semibold text-navy-900">CRAD Lending Test</h1>
            <p className="mt-1 text-sm text-gray-500">
              Three Lending Test outputs per CRA Lending Test methodology: Assessment Area Concentration, Geographic
              Distribution, and Borrower's Profile Distribution — for HMDA, small business, small farm, and consumer
              loans. Computed live from your HMDA validations and CRA loan data.
            </p>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <ModuleIntro storageKey="cra-lending-test" title="What is the Lending Test?">
          <p>
            Three CRA Lending Test outputs, computed live from your HMDA, small business, small farm, and consumer loan
            data: <span className="font-medium">Assessment Area Concentration</span> (lending inside vs. outside your AAs),{' '}
            <span className="font-medium">Geographic Distribution</span> (by Census tract income), and{' '}
            <span className="font-medium">Borrower's Profile</span> (by borrower income or business revenue).
            {isLargeBank
              ? ' The Loan-to-Deposit ratio is not a Lending Test criterion for a Large Bank, so it is not shown; your peer institutions are kept for the Community Development tests.'
              : <> The <span className="font-medium">Loan-to-Deposit summary</span> below compares your bank to its peers from FDIC Call Reports.</>}
          </p>
          <ModuleIntro.Points
            items={[
              <>
                <span className="font-medium text-gray-900">Pick each year &amp; product</span> (or <span className="font-medium">All CRA</span>), then
                <span className="font-medium"> Generate Tables</span>. CRA evaluations usually span multiple years — add each one.
              </>,
              <>
                <span className="font-medium text-gray-900">Demographic vs. market benchmark:</span> optionally
                <span className="font-medium"> Preview</span> the CFPB market aggregate, then <span className="font-medium">Apply</span> it
                to add the market column to Geographic &amp; Borrower distribution.
              </>,
              ...(isLargeBank ? [] : [
                <>
                  <span className="font-medium text-gray-900">Loan-to-Deposit:</span> sourced from FDIC Call Reports (your FDIC
                  cert + last exam date). Pick the comparison peers under the LTD summary.
                </>,
              ]),
            ]}
          />
        </ModuleIntro>
      </div>

      {/* Run Controls — one multi-year / multi-product selection drives the whole report */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-1">Generate Lending Test Tables</h3>
        <p className="text-xs text-gray-500 mb-3">
          Add each year and product (CRA evaluations typically span multiple). Generate stacks them across AA
          Concentration, Geographic Distribution, and Borrower's Profile, with combined totals.
        </p>

        {/* Selected years/products */}
        <div className="flex flex-wrap gap-2 mb-3">
          {items.map((it, idx) => (
            <span key={idx} className="inline-flex items-center gap-1.5 rounded-full bg-ocean-50 border border-ocean-200 px-3 py-1 text-xs text-ocean-800">
              {it.year} {itemLabel(it.product)}
              <button onClick={() => removeItem(idx)} className="text-ocean-400 hover:text-red-600" title="Remove">×</button>
            </span>
          ))}
          {items.length === 0 && <span className="text-xs text-gray-400">No years/products yet — add one below.</span>}
        </div>

        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Product</label>
            <select value={draftProduct} onChange={(e) => setDraftProduct(e.target.value as any)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm">
              <option value="ALL_CRA">All CRA (HMDA, Small Business, Small Farm, Consumer)</option>
              <option value="HMDA">HMDA</option>
              <option value="SB">Small Business</option>
              <option value="SF">Small Farm</option>
              <option value="SBSF">Small Business &amp; Small Farm</option>
              <option value="CONSUMER">Consumer (Types of Loan 05–08)</option>
              <option value="OTHER_SB">Other Loans for Small Business Purposes (03)</option>
              <option value="OTHER_LOAN">Other Loan Data (09)</option>
            </select>
          </div>
          {(draftProduct === 'HMDA' || draftProduct === 'ALL_CRA') && (
            <div className="flex-1 min-w-[240px]">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                HMDA validation{draftProduct === 'ALL_CRA' && <span className="font-normal text-gray-400"> (to include HMDA)</span>}
              </label>
              <select value={draftValidation} onChange={(e) => onPickValidation(e.target.value)}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm">
                <option value="">Select an HMDA validation…</option>
                {validations.map((v: any) => (
                  <option key={v.id} value={v.id}>
                    {isYearMissing(v.calendarYear) ? '⚠ no year detected' : v.calendarYear} — {v.totalLoans} loans ({v.status})
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Year</label>
            <input type="number" min={2018} max={new Date().getFullYear()} value={draftYear}
              onChange={(e) => setDraftYear(e.target.value)} placeholder="e.g., 2024"
              className="block w-28 rounded-md border border-gray-300 px-3 py-2 text-sm" />
          </div>
          <button onClick={addItem}
            className="rounded-md border border-ocean-300 px-3 py-2 text-sm font-medium text-ocean-700 hover:bg-ocean-50">
            + Add year/product
          </button>
          <button onClick={() => runMutation.mutate()} disabled={runMutation.isPending || items.length === 0}
            className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium bg-ocean-600 text-white hover:bg-ocean-700 disabled:opacity-50">
            <PlayIcon className="h-4 w-4" />
            {runMutation.isPending ? 'Computing…' : 'Generate Tables'}
          </button>

          {hmdaYears.length > 0 && (
            <div className="flex flex-col">
              <span className="text-xs font-medium text-gray-500 mb-1">Market benchmark (optional)</span>
              <div className="inline-flex items-center rounded-md border border-ocean-300 bg-white overflow-hidden">
                <button onClick={() => pullAggregateMutation.mutate({ persist: false })}
                  disabled={pullAggregateMutation.isPending}
                  title="Preview the HMDA market aggregate (CFPB Data Browser) for every selected year — no save"
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-ocean-700 hover:bg-ocean-50 disabled:opacity-50">
                  {pullAggregateMutation.isPending && !pullAggregateMutation.variables?.persist ? 'Pulling…' : 'Preview Market Aggregate'}
                </button>
                <span className="h-5 w-px bg-ocean-200" />
                <button onClick={() => pullAggregateMutation.mutate({ persist: true })}
                  disabled={pullAggregateMutation.isPending}
                  title="Apply the CFPB market aggregate for every selected year"
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-ocean-700 hover:bg-ocean-50 disabled:opacity-50">
                  {pullAggregateMutation.isPending && pullAggregateMutation.variables?.persist ? 'Applying…' : 'Apply'}
                </button>
              </div>
            </div>
          )}
          <p className="basis-full text-xs text-gray-500 mt-1">
            <span className="font-semibold text-gray-600">1.</span> Add your years/products and Generate.{' '}
            <span className="font-semibold text-gray-600">2.</span> Optionally Preview the CFPB market aggregate to reconcile, then{' '}
            <span className="font-semibold text-gray-600">3.</span> Apply to add the market benchmark to GD &amp; Borrower's Profile (per year).
          </p>
          {/* At-a-glance: which years actually have a saved market aggregate (answers "is 2025 pulled?"). */}
          <div className="basis-full text-xs mt-1">
            {savedAggregateYears.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="font-semibold text-gray-600">Saved market aggregate:</span>
                {savedAggregateYears.map((y) => (
                  <span key={y.dataYear}
                    className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5">
                    {y.dataYear} · {y.totalLoans.toLocaleString()} loans · {y.assessmentAreaCount} AA
                  </span>
                ))}
                {hmdaYears.filter((y) => !savedAggregateYears.some((s) => s.dataYear === y)).map((y) => (
                  <span key={y}
                    title="This HMDA year is selected but has no saved market aggregate yet — click Apply."
                    className="inline-flex items-center rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5">
                    {y} · not applied yet
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-amber-700">
                No market aggregate saved yet{hmdaYears.length ? ` for ${hmdaYears.join(', ')}` : ''} — click <span className="font-semibold">Apply</span> to pull it from the CFPB.
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Result — three unified sections, all multi-year */}
      {result && (
        <div className="space-y-8">
          {/* Export controls */}
          <div className="flex items-center justify-end gap-2">
            <span className="text-xs text-gray-500">Export results:</span>
            <button
              onClick={handleExportExcel}
              disabled={exportingExcel || exportingWord}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="h-3.5 w-3.5" />
              {exportingExcel ? 'Exporting…' : 'Excel (.xlsx)'}
            </button>
            <button
              onClick={handleExportWord}
              disabled={exportingExcel || exportingWord}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="h-3.5 w-3.5" />
              {exportingWord ? 'Exporting…' : 'Word (.docx)'}
            </button>
          </div>
          {(result.bankCategory === 'LARGE' || (result.bankCategory === undefined && isLargeBank))
            ? teamId && <PeerInstitutionsSection teamId={teamId} />
            : result.ltdSummary !== undefined && <LtdRatioSummarySection summary={result.ltdSummary ?? null} teamId={teamId} />}
          <ConcentrationMatrixDisplay data={result.concentration} />
          {result.multiYear.geographicDistribution.length > 0 && (
            <section className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-navy-900 text-white flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg font-bold tracking-wider uppercase text-white">Geographic Distribution — HMDA</h2>
                  <p className="text-xs text-slate-400 mt-1">Loans inside each AA by tract income — bank vs. demographic (OOHU) vs. market aggregate, each year stacked, most-recent first.</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <label className="flex items-center gap-2 text-xs text-slate-300 whitespace-nowrap cursor-pointer select-none">
                    <input type="checkbox" checked={showDollar} onChange={e => setShowDollar(e.target.checked)} className="rounded border-slate-500" />
                    Show results in $
                  </label>
                  <label className="flex items-center gap-2 text-xs text-slate-300 whitespace-nowrap cursor-pointer select-none"
                    title="Also controls whether the Word export includes the Source / Note column">
                    <input type="checkbox" checked={showSourceNote} onChange={e => setShowSourceNote(e.target.checked)} className="rounded border-slate-500" />
                    Show Source / Note (also in Word)
                  </label>
                </div>
              </div>
              <StackedDistribution title="" blocks={result.multiYear.geographicDistribution} bare showDollarVolume={showDollar} showSourceNote={showSourceNote} />
            </section>
          )}
          {result.multiYear.borrowerProfileDistribution.length > 0 && (
            <section className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-navy-900 text-white flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg font-bold tracking-wider uppercase text-white">Borrower's Profile Distribution — HMDA</h2>
                  <p className="text-xs text-slate-400 mt-1">Loans inside each AA by borrower income — bank vs. demographic (Families) vs. market aggregate, each year stacked, most-recent first.</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <label className="flex items-center gap-2 text-xs text-slate-300 whitespace-nowrap cursor-pointer select-none">
                    <input type="checkbox" checked={showDollar} onChange={e => setShowDollar(e.target.checked)} className="rounded border-slate-500" />
                    Show results in $
                  </label>
                  <label className="flex items-center gap-2 text-xs text-slate-300 whitespace-nowrap cursor-pointer select-none"
                    title="Also controls whether the Word export includes the Source / Note column">
                    <input type="checkbox" checked={showSourceNote} onChange={e => setShowSourceNote(e.target.checked)} className="rounded border-slate-500" />
                    Show Source / Note (also in Word)
                  </label>
                </div>
              </div>
              <StackedDistribution title="" blocks={result.multiYear.borrowerProfileDistribution} bare showDollarVolume={showDollar} showSourceNote={showSourceNote} />
            </section>
          )}
          <SbSfDistributionSections result={result.sbSf} />
          <ConsumerDistributionSections result={result.consumer} showDollar={showDollar} />
        </div>
      )}

      {!result && !runMutation.isPending && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <ClipboardDocumentCheckIcon className="mx-auto h-12 w-12 text-gray-300 mb-4" />
          <h3 className="text-base font-semibold text-navy-900">No Tables Generated Yet</h3>
          <p className="mt-1 text-sm text-gray-500 max-w-md mx-auto">
            Add one or more years/products above and click <strong>Generate Tables</strong>. Computation happens live;
            nothing is persisted unless you separately run a CRAD report.
          </p>
        </div>
      )}
    </div>
  );
}

/* ── LTD Ratio Summary (Wallace 2026-06-08) ────────────────────────────────── */

const fmtLtdPct = (v: number | null | undefined) =>
  v == null ? '—' : `${v.toFixed(1)}%`;

function LtdRatioSummarySection({ summary, teamId }: { summary: LtdSummary | null; teamId?: string }) {
  if (!summary) {
    return (
      <section className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-navy-900 text-white">
          <h2 className="text-lg font-bold tracking-wider uppercase text-white">LTD Ratio — Summary</h2>
          <p className="text-xs text-slate-400 mt-1">Loan-to-deposit ratio peer comparison for the evaluation period.</p>
        </div>
        <div className="px-6 py-5 text-sm text-gray-500 italic">
          No LTD ratio data yet — compute a CRAD report to populate this summary. You can pick the
          peer banks below now; they'll fill in when you generate the CRAD.
        </div>
        {teamId && <LtdPeerEditor teamId={teamId} />}
      </section>
    );
  }

  const conclusionColor = (c: string | null) => {
    if (!c) return 'bg-gray-100 text-gray-700';
    if (c === 'More than Reasonable') return 'bg-green-100 text-green-700';
    if (c === 'Reasonable') return 'bg-blue-100 text-blue-700';
    if (c === 'Less than Reasonable') return 'bg-amber-100 text-amber-700';
    if (c === 'Unreasonable') return 'bg-red-100 text-red-700';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <section className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 bg-navy-900 text-white flex items-center gap-4">
        <div className="flex-1">
          <h2 className="text-lg font-bold tracking-wider uppercase text-white">LTD Ratio — Summary</h2>
          <p className="text-xs text-slate-400 mt-1">
            Quarterly average net loan-to-deposit ratio for the evaluation window
            {summary.windowStart && summary.windowEnd && ` (${summary.windowStart} → ${summary.windowEnd})`}.
          </p>
        </div>
        {summary.conclusion && (
          <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${conclusionColor(summary.conclusion)}`}>
            {summary.conclusion}
          </span>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Metric</th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Bank qtly avg.</th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Peer 1 qtly avg.</th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Peer 2 qtly avg.</th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Peer 3 qtly avg.</th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Peer avg.</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-100">
              <td className="px-6 py-4 font-medium text-navy-900">Net Loan-to-Deposit Ratio</td>
              <td className="px-6 py-4 text-right tabular-nums text-navy-900 font-bold">
                {fmtLtdPct(summary.bankAvgPct)}
              </td>
              {[0, 1, 2].map((i) => {
                const peer = summary.peers[i];
                if (!peer || peer.avgPct == null) {
                  return (
                    <td key={i} className="px-6 py-4 text-right tabular-nums text-gray-400 italic text-xs">N/A</td>
                  );
                }
                return (
                  <td key={i} className="px-6 py-4 text-right tabular-nums" title={peer.name ? `${peer.name} (FDIC #${peer.cert})` : `FDIC #${peer.cert}`}>
                    <div className="text-gray-900 font-semibold">{fmtLtdPct(peer.avgPct)}</div>
                    {peer.name && <div className="text-[10px] font-normal text-gray-400 truncate max-w-[140px] ml-auto">{peer.name}</div>}
                  </td>
                );
              })}
              <td className="px-6 py-4 text-right tabular-nums text-gray-900 font-semibold">
                {fmtLtdPct(summary.peerComparisonAvgPct)}
              </td>
            </tr>
          </tbody>
        </table>
        <p className="px-6 py-2 text-[11px] text-gray-400">
          Bank qtly avg. = the institution's quarterly-average net loan-to-deposit ratio (FDIC Call Reports, this window).
          Peer 1 / Peer 2 / Peer 3 = the bank's selected peers (or top-3 asset/loan-mix comparables when none are selected),
          each peer's quarterly-average net LTD over the same window. Peer avg. = the mean of those peers — the basis for the
          conclusion above.
        </p>
        {teamId && <LtdPeerEditor teamId={teamId} />}
      </div>
    </section>
  );
}

/**
 * A Large Bank's peers, without the LTD ratio (Michael Wallace, 2026-11-01): LTD is not a Large Bank Lending
 * Test criterion, but the peer institutions are kept for Community Development Loans, Qualified Investments
 * and Community Development Services.
 */
function PeerInstitutionsSection({ teamId }: { teamId: string }) {
  return (
    <section className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 bg-navy-900 text-white">
        <h2 className="text-lg font-bold tracking-wider uppercase text-white">Peer Institutions</h2>
        <p className="text-xs text-slate-400 mt-1">
          Large Bank: the loan-to-deposit ratio is not a Lending Test criterion. The peer banks are kept for the
          Community Development tests (CD Loans, Qualified Investments, CD Services).
        </p>
      </div>
      <LtdPeerEditor teamId={teamId} largeBank />
    </section>
  );
}

/* ── LTD peer selector — pick up to 3 peer banks for the Peer 1/2/3 columns ─────── */
function LtdPeerEditor({ teamId, largeBank = false }: { teamId: string; largeBank?: boolean }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [peers, setPeers] = useState<Array<{ cert: number; name: string | null }>>([]);
  const [certInput, setCertInput] = useState('');
  const [looking, setLooking] = useState(false);

  const { data: config } = useQuery({
    queryKey: ['cra-config', teamId],
    queryFn: async () => (await craApi.getConfig(teamId)).data,
  });
  const savedCerts: number[] = config?.peerCertNumbers ?? [];

  const saveMutation = useMutation({
    mutationFn: (certs: number[]) => craApi.updateConfig(teamId, { peerCertNumbers: certs }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cra-config', teamId] });
      setEditing(false);
      toast.success(largeBank ? 'Peer banks saved.' : 'Peer banks saved — click “Generate Tables” / recompute the CRAD to refresh the peer LTDs.');
    },
    onError: () => toast.error('Failed to save peer banks'),
  });

  // Seed the editor's working list from saved certs (look up names) when opening.
  const beginEdit = async () => {
    setEditing(true);
    if (savedCerts.length === 0) { setPeers([]); return; }
    const resolved = await Promise.all(savedCerts.slice(0, 3).map(async (cert) => {
      try { return { cert, name: (await teamApi.lookupByFdicCert(cert)).data?.institutionName ?? null }; }
      catch { return { cert, name: null }; }
    }));
    setPeers(resolved);
  };

  const addCert = async () => {
    const cert = Number(certInput.trim());
    if (!Number.isInteger(cert) || cert <= 0) { toast.error('Enter a valid FDIC cert number'); return; }
    if (peers.some((p) => p.cert === cert)) { toast.error('That peer is already in the list'); return; }
    if (peers.length >= 3) { toast.error('Up to 3 peers'); return; }
    setLooking(true);
    try {
      const r = (await teamApi.lookupByFdicCert(cert)).data;
      if (!r?.found) { toast.error(`FDIC cert ${cert} not found in BankFind`); return; }
      setPeers([...peers, { cert, name: r.institutionName ?? null }]);
      setCertInput('');
    } catch {
      toast.error('Could not reach FDIC BankFind — try again');
    } finally {
      setLooking(false);
    }
  };

  if (!editing) {
    return (
      <div className="px-6 pb-4 pt-1 flex items-center gap-2 text-xs text-gray-500">
        <span>
          Peer banks:{' '}
          <span className="font-medium text-gray-700">
            {savedCerts.length > 0 ? `${savedCerts.length} bank-selected` : 'Auto (top 3 comparables)'}
          </span>
        </span>
        <button onClick={beginEdit} className="inline-flex items-center gap-1 text-ocean-600 hover:text-ocean-700 font-medium">
          <PencilSquareIcon className="h-3.5 w-3.5" /> Edit
        </button>
      </div>
    );
  }

  return (
    <div className="mx-6 mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-navy-900">Select peer banks (up to 3)</h3>
        <button onClick={() => setEditing(false)} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="h-4 w-4" /></button>
      </div>
      <p className="mt-1 text-xs text-gray-500">
        Enter each peer's FDIC certificate number. Leave empty to use the top-3 asset/loan-mix comparables automatically.
      </p>

      <ul className="mt-3 space-y-1.5">
        {peers.map((p) => (
          <li key={p.cert} className="flex items-center justify-between rounded-md bg-white border border-gray-200 px-3 py-1.5 text-sm">
            <span className="text-gray-800">{p.name ?? '—'} <span className="text-gray-400">(FDIC #{p.cert})</span></span>
            <button onClick={() => setPeers(peers.filter((x) => x.cert !== p.cert))} className="text-gray-400 hover:text-red-600"><XMarkIcon className="h-4 w-4" /></button>
          </li>
        ))}
        {peers.length === 0 && <li className="text-xs text-gray-400 italic">No peers selected — top-3 comparables will be used.</li>}
      </ul>

      {peers.length < 3 && (
        <div className="mt-3 flex items-center gap-2">
          <input
            type="number"
            value={certInput}
            onChange={(e) => setCertInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addCert(); }}
            placeholder="FDIC cert #"
            className="w-36 rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          />
          <button onClick={addCert} disabled={looking}
            className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            <PlusIcon className="h-4 w-4" /> {looking ? 'Looking up…' : 'Add'}
          </button>
        </div>
      )}

      <div className="mt-4 flex items-center gap-2">
        <button onClick={() => saveMutation.mutate(peers.map((p) => p.cert))} disabled={saveMutation.isPending}
          className="rounded-lg bg-ocean-600 px-4 py-2 text-sm font-medium text-white hover:bg-ocean-700 disabled:opacity-50">
          {saveMutation.isPending ? 'Saving…' : 'Save peers'}
        </button>
        <button onClick={() => { setPeers([]); saveMutation.mutate([]); }} disabled={saveMutation.isPending}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
          Use auto top-3
        </button>
      </div>
    </div>
  );
}

/* ── AA Concentration — Multi-Year × Multi-Product matrix (PE format) ─────── */
interface MatrixRow {
  label: string; product: string; insideCount: number; insideAmount: number;
  outsideCount: number; outsideAmount: number; totalCount: number; totalAmount: number;
  insidePct: number; outsidePct: number; insideAmountPct: number; outsideAmountPct: number; note?: string;
}

function ConcentrationMatrixDisplay({ data }: { data: { rows: MatrixRow[]; combined: MatrixRow } }) {
  return (
    <section className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 bg-navy-900 text-white">
        <h2 className="text-lg font-bold tracking-wider uppercase text-white">Assessment Area Concentration</h2>
        <p className="text-xs text-slate-400 mt-1">
          Lending inside vs. outside delineated assessment areas, by # and $ — each product/year stacked
          (most-recent first) with a TOTAL across all rows. Originations + purchased loans (action_taken 1, 6).
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Product / Year</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider" colSpan={2}>Inside AA</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider" colSpan={2}>Outside AA</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider" colSpan={2}>Total</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {data.rows.map((row, i) => <MatrixFragmentRow key={i} row={row} />)}
            <MatrixFragmentRow row={data.combined} combined />
          </tbody>
        </table>
      </div>
    </section>
  );
}

function MatrixFragmentRow({ row, combined }: { row: MatrixRow; combined?: boolean }) {
  const base = combined ? 'bg-ocean-50 font-semibold' : '';
  return (
    <>
      <tr className={base}>
        <td className="px-4 py-2 text-sm font-medium text-navy-900" rowSpan={2}>
          {row.label}
          {row.note && <span className="block text-[10px] font-normal text-amber-600">{row.note}</span>}
        </td>
        <td className="px-4 py-2 text-sm text-right text-gray-900 tabular-nums">{row.insideCount.toLocaleString()}</td>
        <td className="px-4 py-2 text-sm text-right text-gray-500 tabular-nums">{fmtPct(row.insidePct)}</td>
        <td className="px-4 py-2 text-sm text-right text-gray-900 tabular-nums">{row.outsideCount.toLocaleString()}</td>
        <td className="px-4 py-2 text-sm text-right text-gray-500 tabular-nums">{fmtPct(row.outsidePct)}</td>
        <td className="px-4 py-2 text-sm text-right text-gray-900 tabular-nums font-semibold">{row.totalCount.toLocaleString()}</td>
        <td className="px-4 py-2 text-sm text-right text-gray-500 tabular-nums">{row.totalCount > 0 ? '100%' : '—'}</td>
      </tr>
      <tr className={combined ? 'bg-ocean-50 font-semibold' : 'bg-gray-50'}>
        <td className="px-4 py-2 text-sm text-right text-gray-700 tabular-nums">{fmtCurrency(row.insideAmount)}</td>
        <td className="px-4 py-2 text-sm text-right text-gray-500 tabular-nums">{fmtPct(row.insideAmountPct)}</td>
        <td className="px-4 py-2 text-sm text-right text-gray-700 tabular-nums">{fmtCurrency(row.outsideAmount)}</td>
        <td className="px-4 py-2 text-sm text-right text-gray-500 tabular-nums">{fmtPct(row.outsideAmountPct)}</td>
        <td className="px-4 py-2 text-sm text-right text-gray-700 tabular-nums font-semibold">{fmtCurrency(row.totalAmount)}</td>
        <td className="px-4 py-2 text-sm text-right text-gray-500 tabular-nums">{row.totalAmount > 0 ? '100%' : '—'}</td>
      </tr>
    </>
  );
}

/* ── Section 2 + 3: Distribution Tables (per AA, stacked) ────────────────── */
/**
 * Toggle bar shown above GD/BPD distribution tables. Two optional views per
 * Wallace 2026-04-28 feedback:
 *   - "Show $ volume" — adds dollar count + dollar % rows under the bank.
 *     Off by default because the dollar split has limited analytical value
 *     for GD/BPD (unlike AA Concentration where it's required).
 *   - "Show last exam" — placeholder for prior-period PE rating overlay.
 *     Renders an info row in the table until PE PDF parsing ships.
 */

function DistributionTableBlock({
  row,
  showDollarVolume,
  showLastExam,
  hideAaHeader,
  showSourceNote = true,
}: {
  row: HmdaLendingTestRow;
  showDollarVolume: boolean;
  showLastExam: boolean;
  /** The Source / Note column (Michael Wallace 2026-10-08: toggle on and off). */
  showSourceNote?: boolean;
  /** In the multi-year stacked view the AA name is shown once above the year blocks. */
  hideAaHeader?: boolean;
}) {
  const tiers: Array<keyof TierDistribution> = ['low', 'moderate', 'middle', 'upper', 'unknown'];
  const tierLabels: Record<keyof TierDistribution, string> = {
    low: 'Low',
    moderate: 'Moderate',
    middle: 'Middle',
    upper: 'Upper',
    unknown: 'Unknown',
  };
  // Wallace 2026-04-28 spec: only LMI tiers (Low + Moderate) are color-coded
  // in the comparator rows, since the CRA Lending Test focuses on LMI
  // performance. Middle/Upper/Unknown comparator cells stay neutral.
  const isLmiTier = (t: keyof TierDistribution) => t === 'low' || t === 'moderate';
  // Bank rowSpan grows from 2 (count + count%) to 4 when $ volume is on.
  const bankRowSpan = showDollarVolume ? 4 : 2;

  return (
    <div className={hideAaHeader ? 'px-6 pb-5' : 'px-6 py-5'}>
      {!hideAaHeader && (
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <h3 className="text-base font-semibold text-navy-900">{row.assessmentAreaName}</h3>
          </div>
          {row.conclusion && (
            <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${conclusionColor(row.conclusion)}`}>
              {row.conclusion}
            </span>
          )}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead>
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-44">Series</th>
              {tiers.map((t) => (
                <th key={t} className="px-3 py-2 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">{tierLabels[t]}</th>
              ))}
              <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Total</th>
              {showSourceNote && <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Source / Note</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {/* Bank: count + count % rows always render; $ volume rows render
                only when toggled on (Wallace 2026-04-28 — $ has limited
                analytical value for GD/BPD vs AA Concentration). */}
            <tr className="bg-ocean-50/30">
              <td className="px-3 py-2 font-medium text-navy-900" rowSpan={bankRowSpan}>{row.bank.productLabel}</td>
              {tiers.map((t) => (
                <td key={t} className="px-3 py-2 text-right tabular-nums text-gray-900">{row.bank.counts[t].toLocaleString()}</td>
              ))}
              <td className="px-3 py-2 text-right tabular-nums font-semibold text-gray-900">{row.bank.total.toLocaleString()}</td>
              {showSourceNote && <td className="px-3 py-2 text-xs text-gray-500" rowSpan={bankRowSpan}>Bank HMDA originations + purchased loans (action_taken 1, 6), classified by CRA tract income (Geographic) or borrower income (Borrower Profile).</td>}
            </tr>
            <tr className="bg-ocean-50/30">
              {tiers.map((t) => (
                <td key={t} className="px-3 py-2 text-right tabular-nums text-gray-500">{fmtPct(row.bank.pcts[t])}</td>
              ))}
              <td className="px-3 py-2 text-right tabular-nums text-gray-500">100%</td>
            </tr>
            {showDollarVolume && (
              <>
                <tr className="bg-ocean-50/20">
                  {tiers.map((t) => (
                    <td key={t} className="px-3 py-2 text-right tabular-nums text-gray-700">{fmtCurrency(row.bank.amounts[t])}</td>
                  ))}
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-gray-700">{fmtCurrency(row.bank.totalAmount)}</td>
                </tr>
                <tr className="bg-ocean-50/20">
                  {tiers.map((t) => (
                    <td key={t} className="px-3 py-2 text-right tabular-nums text-gray-500">{fmtPct(row.bank.amountPcts[t])}</td>
                  ))}
                  <td className="px-3 py-2 text-right tabular-nums text-gray-500">100%</td>
                </tr>
              </>
            )}

            {/* Demographic benchmark */}
            <tr>
              <td className="px-3 py-2 font-medium text-navy-900">{row.demographic.label}</td>
              {tiers.map((t) => {
                const colorClass = isLmiTier(t) && row.demographic.pcts
                  ? lmiCompareClass(row.demographic.pcts[t], row.bank.pcts[t])
                  : 'text-gray-700';
                return (
                  <td key={t} className={`px-3 py-2 text-right tabular-nums ${colorClass}`}>
                    {row.demographic.pcts ? fmtPct(row.demographic.pcts[t]) : '—'}
                  </td>
                );
              })}
              <td className="px-3 py-2 text-right tabular-nums text-gray-700">{row.demographic.pcts ? '100%' : '—'}</td>
              {showSourceNote && (
                <td className="px-3 py-2 text-xs text-gray-500">
                  {row.demographic.methodology}
                  {row.demographic.sourceNote && (
                    <div className="mt-1 inline-flex items-start gap-1 text-amber-700">
                      <InformationCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <span>{row.demographic.sourceNote}</span>
                    </div>
                  )}
                </td>
              )}
            </tr>

            {/* Market benchmark — pending */}
            <tr className="bg-amber-50/40">
              <td className="px-3 py-2 font-medium text-navy-900">{row.marketBenchmark.label}</td>
              {tiers.map((t) => {
                const colorClass = isLmiTier(t) && row.marketBenchmark.pcts
                  ? lmiCompareClass(row.marketBenchmark.pcts[t], row.bank.pcts[t])
                  : 'text-amber-700 italic';
                return (
                  <td key={t} className={`px-3 py-2 text-right tabular-nums ${row.marketBenchmark.pcts ? colorClass : 'text-amber-700 italic'}`}>
                    {row.marketBenchmark.pcts ? fmtPct(row.marketBenchmark.pcts[t]) : 'TBD'}
                  </td>
                );
              })}
              <td className="px-3 py-2 text-right text-amber-700 italic">{row.marketBenchmark.pcts ? '100%' : 'TBD'}</td>
              {showSourceNote && <td className="px-3 py-2 text-xs text-amber-700">
                {row.marketBenchmark.pending ? (
                  <div className="inline-flex items-start gap-1 text-gray-500">
                    <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span>Not applied yet — use “Preview Market Aggregate” then “Apply” above to load the {row.marketBenchmark.label} from the CFPB Data Browser. {row.marketBenchmark.methodology}</span>
                  </div>
                ) : (
                  row.marketBenchmark.methodology
                )}
              </td>}
            </tr>

            {showLastExam && (
              <tr className="bg-slate-50">
                <td className="px-3 py-2 font-medium text-navy-900">Last PE — Conclusion</td>
                <td colSpan={tiers.length + 1} className="px-3 py-2 text-sm italic text-slate-500">
                  Pending — prior Performance Evaluation ingestion shipping next iteration. Will display the AA's last exam conclusion (Excellent / Good / Adequate / Poor / Very Poor for Large Bank; comparable scale for Small/ISB).
                </td>
                {showSourceNote && (
                  <td className="px-3 py-2 text-xs text-slate-500">
                    Source: most recent FFIEC-published Performance Evaluation PDF for the institution.
                  </td>
                )}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Multi-Year GD & BPD — stacked per AA (PE format) ─────────────────────── */
interface MultiYearAaBlock { assessmentAreaName: string; years: Array<{ year: number; row: HmdaLendingTestRow | null }> }

function StackedDistribution({ title, blocks, bare, showDollarVolume = false, showSourceNote = false }: { title: string; blocks: MultiYearAaBlock[]; bare?: boolean; showDollarVolume?: boolean; showSourceNote?: boolean }) {
  return (
    <div className={bare ? '' : 'border border-gray-100 rounded-lg overflow-hidden'}>
      {!bare && (
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
          <h3 className="text-sm font-bold uppercase tracking-wider text-navy-900">{title}</h3>
        </div>
      )}
      <div className="divide-y divide-gray-200">
        {blocks.map((block) => (
          <div key={block.assessmentAreaName} className="py-2">
            <div className="px-6 pt-3 pb-1">
              <h4 className="text-base font-semibold text-navy-900">{block.assessmentAreaName}</h4>
            </div>
            {block.years.map(({ year, row }) => (
              <div key={year}>
                <div className="px-6 pt-2 text-xs font-semibold uppercase tracking-wider text-ocean-700">{year}</div>
                {row
                  ? <DistributionTableBlock row={row} showDollarVolume={showDollarVolume} showLastExam={false} hideAaHeader showSourceNote={showSourceNote} />
                  : <div className="px-6 py-2 text-xs text-gray-400 italic">No data for {year} in this assessment area.</div>}
              </div>
            ))}
          </div>
        ))}
        {blocks.length === 0 && <div className="px-6 py-4 text-sm text-gray-400">No assessment areas.</div>}
      </div>
    </div>
  );
}

/* ── SB/SF GD & BPD (bank data) — Michael Wallace 2026-07-26 ────────────────
 * SB tables first, SF underneath, each product stacked most-recent year first.
 * GD  = inside-AA loans by CRA tract income level (Low/Moderate/Middle/Upper/Unknown).
 * BPD = a Gross-Annual-Revenue × loan-amount matrix (SB tops at $1,000K, SF at $500K).
 * Aggregate/demographic benchmark rows are a deliberate follow-up ("bank data first").
 */

/** Display label for a BPD loan-amount tier key, in Michael's spreadsheet wording. */
function bpdTierLabel(key: string): string {
  switch (key) {
    case '<$100': return '< $100';
    case '$100-$250': return '> $100 and < $250';
    case '$250-$1000': return '> $250 and < $1,000';
    case '$250-$500': return '> $250 and < $500';
    default: return key;
  }
}

function SbSfGdTable({ row, product }: { row: SbSfGdYear; product: 'SB' | 'SF' }) {
  const total = row.total;
  return (
    <div className="px-6 pb-4">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead>
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-56">Product</th>
              {GD_INCOME_LEVELS.map(l => (
                <th key={l} className="px-3 py-2 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">{l}</th>
              ))}
              <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            <tr className="bg-ocean-50/30">
              <td className="px-3 py-2 font-medium text-navy-900" rowSpan={2}>{row.year} {product} Loans</td>
              {GD_INCOME_LEVELS.map(l => (
                <td key={l} className="px-3 py-2 text-right tabular-nums text-gray-900">{row.distribution[l].toLocaleString()}</td>
              ))}
              <td className="px-3 py-2 text-right tabular-nums font-semibold text-gray-900">{total.toLocaleString()}</td>
            </tr>
            <tr className="bg-ocean-50/30">
              {GD_INCOME_LEVELS.map(l => (
                <td key={l} className="px-3 py-2 text-right tabular-nums text-gray-500">{fmtPct(total > 0 ? row.distribution[l] / total : 0)}</td>
              ))}
              <td className="px-3 py-2 text-right tabular-nums text-gray-500">{total > 0 ? '100%' : '—'}</td>
            </tr>
            {row.aggregate ? (
              <tr className="bg-amber-50/40">
                <td className="px-3 py-2 font-medium text-navy-900">
                  {row.aggregate.sourceYear} {PRODUCT_LABEL[product]} Aggregate
                  {row.aggregate.sourceYear !== row.year && (
                    <span className="ml-1 text-xs font-normal text-amber-700">({row.year} not yet published)</span>
                  )}
                  <span className="ml-1 text-[10px] font-normal uppercase tracking-wide text-gray-400">· {aggSourceTag(row.aggregate.source)}</span>
                </td>
                {GD_INCOME_LEVELS.map(l => {
                  const v = row.aggregate![GD_AGG_KEY[l]];
                  return <td key={l} className="px-3 py-2 text-right tabular-nums text-gray-700">{v == null ? '—' : fmtPct(v)}</td>;
                })}
                <td className="px-3 py-2 text-right tabular-nums text-gray-500">100%</td>
              </tr>
            ) : (
              <tr className="bg-amber-50/40">
                <td className="px-3 py-2 font-medium text-navy-900">{row.year} {PRODUCT_LABEL[product]} Aggregate</td>
                <td colSpan={GD_INCOME_LEVELS.length + 1} className="px-3 py-2 text-xs text-amber-700">
                  <span className="inline-flex items-start gap-1 italic">
                    <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    No {PRODUCT_LABEL[product]} aggregate loaded for {row.year} or {row.year - 1} in this AA. FFIEC
                    publishes a year&apos;s CRA aggregate the following October–December, so {row.year} compares to
                    the {row.year - 1} aggregate until then — but neither is loaded yet.
                  </span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SbSfBpdMatrix({ row, product }: { row: SbSfBpdYear; product: 'SB' | 'SF' }) {
  const total = row.total;
  const pct = (n: number) => fmtPct(total > 0 ? n / total : 0);
  return (
    <div className="px-6 pb-4">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead>
            <tr>
              <th rowSpan={2} className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider align-bottom w-56">
                Gross Revenue: {row.year} {product} Loans
              </th>
              <th colSpan={row.tierKeys.length * 2} className="px-3 py-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200">
                Loan Amount in $000s
              </th>
              <th colSpan={2} className="px-3 py-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200">Total</th>
            </tr>
            <tr>
              {row.tierKeys.map(k => (
                <th key={k} colSpan={2} className="px-3 py-2 text-center text-xs font-semibold text-gray-600">{bpdTierLabel(k)}</th>
              ))}
              <th colSpan={2} className="px-3 py-2 text-center text-xs font-semibold text-gray-600">#/%</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {GAR_ROWS.map(gar => {
              const cells = row.byGarAndAmount[gar.code] || {};
              const rowTotal = row.garTotals[gar.code] || 0;
              return (
                <tr key={gar.code} className="bg-ocean-50/20">
                  <td className="px-3 py-2 font-medium text-navy-900">{gar.label}</td>
                  {row.tierKeys.map(k => (
                    <FragmentCells key={k} count={cells[k] || 0} pctText={pct(cells[k] || 0)} />
                  ))}
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-gray-900">{rowTotal.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-500">{pct(rowTotal)}</td>
                </tr>
              );
            })}
            <tr className="bg-gray-50 font-semibold">
              <td className="px-3 py-2 text-navy-900">TOTAL</td>
              {row.tierKeys.map(k => (
                <FragmentCells key={k} count={row.tierTotals[k] || 0} pctText={pct(row.tierTotals[k] || 0)} bold />
              ))}
              <td className="px-3 py-2 text-right tabular-nums text-navy-900">{total.toLocaleString()}</td>
              <td className="px-3 py-2 text-right tabular-nums text-gray-600">{total > 0 ? '100%' : '—'}</td>
            </tr>
            {row.aggregate && row.aggregate.garLessThan1MPct != null ? (
              <tr className="bg-amber-50/40">
                <td className="px-3 py-2 font-medium text-navy-900">
                  {row.aggregate.sourceYear} {PRODUCT_LABEL[product]} Aggregate — GAR ≤ $1M
                  {row.aggregate.sourceYear !== row.year && (
                    <span className="ml-1 text-xs font-normal text-amber-700">({row.year} not yet published)</span>
                  )}
                  <span className="ml-1 text-[10px] font-normal uppercase tracking-wide text-gray-400">· {aggSourceTag(row.aggregate.source)}</span>
                </td>
                <td colSpan={row.tierKeys.length * 2 + 2} className="px-3 py-2 text-right tabular-nums font-semibold text-gray-800">
                  {fmtPct(row.aggregate.garLessThan1MPct)}
                </td>
              </tr>
            ) : (
              <tr className="bg-amber-50/40">
                <td className="px-3 py-2 font-medium text-navy-900">{row.year} {PRODUCT_LABEL[product]} Aggregate — GAR ≤ $1M</td>
                <td colSpan={row.tierKeys.length * 2 + 2} className="px-3 py-2 text-xs text-amber-700">
                  <span className="inline-flex items-start gap-1 italic">
                    <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    No {PRODUCT_LABEL[product]} aggregate loaded for {row.year} or {row.year - 1} in this AA. FFIEC
                    publishes a year&apos;s CRA aggregate the following October–December, so {row.year} compares to
                    the {row.year - 1} aggregate until then — but neither is loaded yet.
                  </span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** One loan-amount tier renders as a count cell + a percent cell (Michael's #/% column pairs). */
function FragmentCells({ count, pctText, bold }: { count: number; pctText: string; bold?: boolean }) {
  return (
    <>
      <td className={`px-3 py-2 text-right tabular-nums ${bold ? 'text-navy-900' : 'text-gray-900'}`}>{count.toLocaleString()}</td>
      <td className="px-3 py-2 text-right tabular-nums text-gray-500">{pctText}</td>
    </>
  );
}

/** Renders a stacked (per-AA, per-year) section for one SB/SF criterion. */
function SbSfStackedSection<T>({
  title,
  subtitle,
  blocks,
  product,
  renderYear,
}: {
  title: string;
  subtitle: string;
  blocks: Array<SbSfAaBlock<T>>;
  product: 'SB' | 'SF';
  renderYear: (row: T, product: 'SB' | 'SF') => JSX.Element;
}) {
  if (blocks.length === 0) return null;
  return (
    <section className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 bg-navy-900 text-white">
        <h2 className="text-lg font-bold tracking-wider uppercase text-white">{title}</h2>
        <p className="text-xs text-slate-400 mt-1">{subtitle}</p>
      </div>
      <div className="divide-y divide-gray-200">
        {blocks.map(block => (
          <div key={block.assessmentAreaName} className="py-2">
            <div className="px-6 pt-3 pb-1">
              <h4 className="text-base font-semibold text-navy-900">{block.assessmentAreaName}</h4>
            </div>
            {block.years.map(({ year, row }) => (
              <div key={year}>
                <div className="px-6 pt-2 text-xs font-semibold uppercase tracking-wider text-ocean-700">{year} {product} Loans</div>
                {row
                  ? renderYear(row, product)
                  : <div className="px-6 py-2 text-xs text-gray-400 italic">No {product} loans for {year} in this assessment area.</div>}
              </div>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

function SbSfDistributionSections({ result }: { result?: SbSfLendingResult }) {
  const products = result?.products ?? [];
  if (products.length === 0) return null;
  return (
    <>
      {/* Geographic Distribution — SB first, then SF (Michael's GD tab order). */}
      {products.map(p => (
        <SbSfStackedSection<SbSfGdYear>
          key={`gd-${p.product}`}
          title={`Geographic Distribution — ${PRODUCT_LABEL[p.product]}`}
          subtitle={`${PRODUCT_LABEL[p.product]} loans inside each AA by CRA tract income level, each year stacked, most-recent first.`}
          blocks={p.geographicDistribution}
          product={p.product}
          renderYear={(gdRow, prod) => <SbSfGdTable row={gdRow} product={prod} />}
        />
      ))}
      {/* Borrower's Profile — SB first, then SF (Michael's BPD tab order). */}
      {products.map(p => (
        <SbSfStackedSection<SbSfBpdYear>
          key={`bpd-${p.product}`}
          title={`Borrower's Profile Distribution — ${PRODUCT_LABEL[p.product]}`}
          subtitle={`${PRODUCT_LABEL[p.product]} loans inside each AA by gross annual revenue × loan amount ($000s), each year stacked, most-recent first.`}
          blocks={p.borrowerProfileDistribution}
          product={p.product}
          renderYear={(bpdRow, prod) => <SbSfBpdMatrix row={bpdRow} product={prod} />}
        />
      ))}
    </>
  );
}

/* ── Consumer GD & BPD (Michael Wallace, 2026-10-06) ─────────────────────────
 * One GD and one BPD section per Type of Loan, each AA stacked by year. Bank
 * distribution only: no demographic or aggregate comparator has been chosen for
 * consumer loans yet, and the tables say so rather than imply one. */
function ConsumerBandTable({ label, year, bands, showDollar, comparator }: { label: string; year: number; bands: BandCounts; showDollar: boolean; comparator?: ComparatorRow | null }) {
  const pctOf = (n: number, d: number) => (d > 0 ? fmtPct(n / d) : '—');
  return (
    <div className="px-6 pb-4">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead>
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-56">Product</th>
              {BAND_KEYS.map(b => (
                <th key={b} className="px-3 py-2 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">{b}</th>
              ))}
              <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            <tr className="bg-ocean-50/30">
              <td className="px-3 py-2 font-medium text-navy-900" rowSpan={showDollar ? 4 : 2}>{year} {label}</td>
              {BAND_KEYS.map(b => <td key={b} className="px-3 py-2 text-right tabular-nums text-gray-900">{bands.count[b].toLocaleString()}</td>)}
              <td className="px-3 py-2 text-right tabular-nums font-semibold text-gray-900">{bands.totalCount.toLocaleString()}</td>
            </tr>
            <tr className="bg-ocean-50/30">
              {BAND_KEYS.map(b => <td key={b} className="px-3 py-2 text-right tabular-nums text-gray-500">{pctOf(bands.count[b], bands.totalCount)}</td>)}
              <td className="px-3 py-2 text-right tabular-nums text-gray-500">{bands.totalCount > 0 ? '100%' : '—'}</td>
            </tr>
            {showDollar && (
              <>
                <tr className="bg-ocean-50/30">
                  {BAND_KEYS.map(b => <td key={b} className="px-3 py-2 text-right tabular-nums text-gray-900">{fmtCurrency(bands.amount[b])}</td>)}
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-gray-900">{fmtCurrency(bands.totalAmount)}</td>
                </tr>
                <tr className="bg-ocean-50/30">
                  {BAND_KEYS.map(b => <td key={b} className="px-3 py-2 text-right tabular-nums text-gray-500">{pctOf(bands.amount[b], bands.totalAmount)}</td>)}
                  <td className="px-3 py-2 text-right tabular-nums text-gray-500">{bands.totalAmount > 0 ? '100%' : '—'}</td>
                </tr>
              </>
            )}
            {comparator && (
              <tr className="bg-amber-50/40">
                <td className="px-3 py-2 font-medium text-navy-900">
                  {comparator.label}
                  <span className="ml-1 text-xs font-normal text-gray-500">({comparator.detail})</span>
                </td>
                {BAND_KEYS.map(b => (
                  <td key={b} className="px-3 py-2 text-right tabular-nums text-gray-700">
                    {comparator.pct[b] == null ? '—' : fmtPct(comparator.pct[b])}
                  </td>
                ))}
                <td className="px-3 py-2 text-right tabular-nums text-gray-500">100%</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ConsumerStackedSection({ product, criterion, showDollar }: { product: ConsumerProductResult; criterion: 'geographic' | 'borrower'; showDollar: boolean }) {
  const isGd = criterion === 'geographic';
  const isConsumer = product.product === 'CONSUMER';
  const subtitle = isGd
    ? `${product.label} loans inside each AA by CRA tract income level, each year stacked, most-recent first.`
      + (isConsumer ? " Compared to the AA's households by tract income level." : '')
    : `${product.label} loans inside each AA by borrower income as a share of the FFIEC Median Family Income, each year stacked, most-recent first. A reported $0 income, or no income reported, is Unknown.`
      + " Compared to the AA's households by income level against the MSA/MD median household income.";
  const comparatorFor = (row: ConsumerYearRow): ComparatorRow | null => {
    if (isGd) {
      const h = row.householdsByTract;
      return h ? { label: '% of AA Households', detail: `${h.sourceYear}, ${h.households.toLocaleString()} households`, pct: h.pct } : null;
    }
    const h = row.householdsByIncome;
    if (!h) return null;
    const mhi = h.medianHouseholdIncome != null ? `, MHI ${fmtCurrency(h.medianHouseholdIncome)}` : '';
    return { label: '% of AA Households by Income', detail: `${h.sourceYear}${mhi}`, pct: h.pct };
  };
  return (
    <section className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 bg-navy-900 text-white">
        <h2 className="text-lg font-bold tracking-wider uppercase text-white">
          {isGd ? 'Geographic Distribution' : "Borrower's Profile Distribution"} — {product.label}
        </h2>
        <p className="text-xs text-slate-400 mt-1">{CONSUMER_SCOPE[product.product]} {subtitle}</p>
      </div>
      <div className="divide-y divide-gray-200">
        {product.assessmentAreas.map(block => (
          <div key={block.assessmentAreaName} className="py-2">
            <div className="px-6 pt-3 pb-1">
              <h4 className="text-base font-semibold text-navy-900">{block.assessmentAreaName}</h4>
            </div>
            {block.years.map(({ year, row }) => {
              const bands = row ? (isGd ? row.geographic : row.borrower) : null;
              return (
                <div key={year}>
                  {row && bands
                    ? (
                      <>
                        <ConsumerBandTable label={product.label} year={year} bands={bands} showDollar={showDollar}
                          comparator={isConsumer ? comparatorFor(row) : null} />
                        {isConsumer && !comparatorFor(row) && (
                          <div className="px-6 pb-3 -mt-2 text-xs text-amber-700 italic">
                            No Census household data is loaded for this assessment area in {year}, so there is no
                            comparator row. Household figures arrive with a re-ingest of that year&apos;s FFIEC Census file.
                          </div>
                        )}
                      </>
                    )
                    : <div className="px-6 py-2 text-xs text-gray-400 italic">No {product.label} loans for {year} in this assessment area.</div>}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </section>
  );
}

function ConsumerDistributionSections({ result, showDollar }: { result?: ConsumerLendingResult; showDollar: boolean }) {
  const products = result?.products ?? [];
  if (products.length === 0) return null;
  return (
    <>
      {products.map(p => <ConsumerStackedSection key={`gd-${p.product}`} product={p} criterion="geographic" showDollar={showDollar} />)}
      {/* 03 and 09 have no Borrower's Profile basis yet — Geographic Distribution only. */}
      {products.filter(p => p.hasBorrowerProfile).map(p => (
        <ConsumerStackedSection key={`bpd-${p.product}`} product={p} criterion="borrower" showDollar={showDollar} />
      ))}
    </>
  );
}
